/**
 * assets/dm-expand-grid.js — collapses a card grid to its first row, with a
 * "Show more" button and a bottom fade indicating hidden content.
 *
 * WHY MEASURED, NOT A FIXED HEIGHT. The grid's row height is content-driven
 * (card copy length varies) and the column count changes at the mobile
 * breakpoint, so a single CSS `max-height` cannot express "exactly row one"
 * across instances or viewport widths. This measures the real DOM instead:
 * the collapsed height is the tallest bottom edge among the items that share
 * the first item's own top edge — i.e. row one, however tall its tallest
 * card turns out to be, at whatever column count the grid is currently
 * rendering.
 *
 * RE-MEASURED ON RESIZE (`observeResize`, dm-core.js) — a column-count change
 * at the ≤749px breakpoint changes which items ARE row one, and card copy
 * can reflow to a different number of lines at a narrower width even within
 * the same column count. Reads are coalesced through `this.frame()` so a
 * resize never triggers more than one measurement per animation frame.
 *
 * WHY THE OBSERVER WATCHES THE CARDS, NOT THIS ELEMENT. Once collapsed,
 * `this` element's own rendered height IS the (possibly still-too-small)
 * collapsed height — its only sized child is capped by the very
 * `max-height` this file just set. A late reflow inside that capped child
 * (the custom display/mono fonts swapping in after their first, wider
 * fallback-font layout, in particular) would never change `this`'s own box,
 * so a `ResizeObserver` on `this` cannot see it and the wrong measurement
 * would stick. The grid's CHILDREN are never themselves clipped — only
 * their parent gets `overflow: hidden` — so observing them catches a
 * post-collapse font swap that observing the wrapper would miss.
 *
 * `document.fonts.ready` covers the same case a second way, for a browser
 * where the swap does not itself trigger a `ResizeObserver` entry on
 * whatever generated it (a text reflow with no element boundary change).
 *
 * CSS OWNS THE FADE, NOT THIS FILE. `--dm-expand-collapsed-h` is the one
 * value JS contributes; `section-dm-card-grid.css` reads it for both the
 * `max-height` clamp and the mask-image fade, and owns the expand/collapse
 * transition. This file never toggles a visual property directly — only the
 * custom property and the `is-expanded` class/`aria-expanded` state.
 *
 * Classic script, ES2020, built on `DMElement` (dm-core.js).
 */
(function () {
  'use strict';

  class DMExpandGrid extends DMElement {
    init() {
      this.grid = this.querySelector(':scope > [data-expand-grid]');
      this.button = this.querySelector(':scope > [data-expand-toggle]');
      if (!this.grid || !this.button) return;

      this.showLabel = this.button.dataset.showLabel || this.button.textContent;
      this.hideLabel = this.button.dataset.hideLabel || this.showLabel;
      this.expanded = false;

      this.on(this.button, 'click', () => this.toggle());

      // Each card observed individually — see the file header on why the
      // wrapper itself cannot be the observed target post-collapse.
      const onResize = () => this.frame(() => this.measure());
      Array.from(this.grid.children).forEach((item) => {
        this.track(new ResizeObserver(onResize)).observe(item);
      });

      this.measure();

      // A second pass once web fonts are actually in, for the browsers/cases
      // a font swap does not itself fire as a ResizeObserver entry.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.frame(() => this.measure()));
      }
    }

    /** Row one's own bottom edge, relative to the grid's top — see the file
     *  header for why this cannot be a fixed number. */
    measure() {
      if (this.expanded) return;

      const items = Array.from(this.grid.children);
      if (!items.length) return;

      const gridTop = this.grid.getBoundingClientRect().top;
      const firstTop = items[0].getBoundingClientRect().top;
      let rowBottom = 0;

      for (const item of items) {
        const rect = item.getBoundingClientRect();
        // 2px tolerance: sub-pixel layout can place "the same row" a
        // fraction of a pixel apart between items.
        if (rect.top > firstTop + 2) break;
        rowBottom = Math.max(rowBottom, rect.bottom - gridTop);
      }

      this.style.setProperty('--dm-expand-collapsed-h', Math.ceil(rowBottom) + 'px');

      // Nothing to expand — every item already fits on row one.
      const allFit = items.every((item) => item.getBoundingClientRect().bottom - gridTop <= rowBottom + 2);
      this.classList.toggle('dm-expand-grid--single-row', allFit);
    }

    toggle() {
      this.expanded = !this.expanded;
      this.classList.toggle('is-expanded', this.expanded);
      this.attr(this.button, 'aria-expanded', this.expanded);
      this.button.textContent = this.expanded ? this.hideLabel : this.showLabel;
      if (!this.expanded) this.measure();
    }
  }

  DMElement.define('dm-expand-grid', DMExpandGrid);
})();
